import React, { useState } from "react";
import { Link } from "react-router-dom";
import Content from "@/components/specific/Content";
import { useDocumentTitle } from "@/utils/hooks/useDocumentTitle";
import { postWhistleblower } from "@/utils/apis/public";

const JENIS_PELANGGARAN = [
  { value: "", label: "-- Pilih Jenis Pelanggaran --" },
  { value: "korupsi", label: "Korupsi" },
  { value: "gratifikasi", label: "Gratifikasi" },
  { value: "penyalahgunaan_wewenang", label: "Penyalahgunaan Wewenang" },
  { value: "kecurangan", label: "Kecurangan / Fraud" },
  { value: "pelanggaran_etika", label: "Pelanggaran Kode Etik" },
  { value: "konflik_kepentingan", label: "Konflik Kepentingan" },
  { value: "lainnya", label: "Lainnya" },
];

const INITIAL_FORM = {
  is_anonymous: false,
  nama_pelapor: "",
  no_hp_pelapor: "",
  jenis_pelanggaran: "",
  nama_terlapor: "",
  jabatan_terlapor: "",
  lokasi_kejadian: "",
  tanggal_kejadian: "",
  uraian_kejadian: "",
};

const REQUIRED_FIELDS_NAMED = {
  nama_pelapor: "Nama pelapor",
  no_hp_pelapor: "Nomor handphone pelapor",
  jenis_pelanggaran: "Jenis pelanggaran",
  nama_terlapor: "Nama / jabatan terlapor",
  uraian_kejadian: "Uraian kejadian",
};

const REQUIRED_FIELDS_ANONYMOUS = {
  jenis_pelanggaran: "Jenis pelanggaran",
  nama_terlapor: "Nama / jabatan terlapor",
  uraian_kejadian: "Uraian kejadian",
};

function FieldError({ message }) {
  if (!message) return null;
  return <p className="field-error text-xs text-red-500 mt-1">{message}</p>;
}

function SectionTitle({ icon, title }) {
  return (
    <div className="flex items-center gap-3 mb-5 pb-3 border-b border-gray-100">
      <div className="bg-[#095393]/10 p-2 rounded-lg text-[#095393]">
        {icon}
      </div>
      <h3 className="text-base font-bold text-gray-800">{title}</h3>
    </div>
  );
}

function FormField({ label, required, error, children }) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
      </label>
      {children}
      <FieldError message={error} />
    </div>
  );
}

const inputClass = (error) =>
  `w-full px-3 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#095393] focus:border-transparent transition-colors ${
    error ? "border-red-400 bg-red-50" : "border-gray-300 bg-white"
  }`;

const selectClass = (error) =>
  `w-full px-3 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#095393] focus:border-transparent transition-colors appearance-none bg-white ${
    error ? "border-red-400 bg-red-50" : "border-gray-300"
  }`;

function WhistleblowerSystem() {
  useDocumentTitle("Whistleblowing System - Perumda Air Minum Cilegon Mandiri");

  const [form, setForm] = useState(INITIAL_FORM);
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [submitMessage, setSubmitMessage] = useState("");
  const [reportCode, setReportCode] = useState("");

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === "checkbox" ? checked : value;
    setForm((prev) => ({ ...prev, [name]: newValue }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validate = () => {
    const newErrors = {};
    const requiredFields = form.is_anonymous
      ? REQUIRED_FIELDS_ANONYMOUS
      : REQUIRED_FIELDS_NAMED;

    Object.entries(requiredFields).forEach(([key, label]) => {
      if (!form[key] || String(form[key]).trim() === "") {
        newErrors[key] = `${label} wajib diisi`;
      }
    });

    if (
      !form.is_anonymous &&
      form.no_hp_pelapor &&
      !/^\d{8,15}$/.test(form.no_hp_pelapor)
    ) {
      newErrors.no_hp_pelapor = "Nomor handphone tidak valid (8-15 digit)";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      const firstErrorEl = document.querySelector(".field-error");
      if (firstErrorEl)
        firstErrorEl.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }

    setIsLoading(true);
    setSubmitStatus(null);

    try {
      const payload = {
        is_anonymous: form.is_anonymous,
        nama_pelapor: form.is_anonymous ? "" : form.nama_pelapor,
        no_hp_pelapor: form.is_anonymous ? "" : form.no_hp_pelapor,
        jenis_pelanggaran: form.jenis_pelanggaran,
        nama_terlapor: form.nama_terlapor,
        jabatan_terlapor: form.jabatan_terlapor,
        lokasi_kejadian: form.lokasi_kejadian,
        tanggal_kejadian: form.tanggal_kejadian || null,
        uraian_kejadian: form.uraian_kejadian,
        path_bukti: "",
        nama_file_bukti: "",
      };

      const response = await postWhistleblower(payload);
      const code =
        response?.data?.kode_laporan ||
        response?.data?.data?.kode_laporan ||
        "";
      setReportCode(code);
      setSubmitStatus("success");
      setSubmitMessage(
        response?.data?.message ||
          "Laporan Anda berhasil dikirim. Terima kasih atas partisipasi Anda.",
      );
      setForm(INITIAL_FORM);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      setSubmitStatus("error");
      const errMsg =
        err?.response?.data?.message ||
        err?.message ||
        "Terjadi kesalahan. Silakan coba lagi.";
      setSubmitMessage(errMsg);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      {/* Hero */}
      <div
        className="relative bg-[#095393] overflow-hidden w-screen z-0"
        style={{
          marginLeft: "calc(50% - 50vw)",
          marginRight: "calc(50% - 50vw)",
          width: "100vw",
          maxWidth: "100vw",
        }}
      >
        <div className="absolute bottom-0 left-0 right-0">
          <svg
            className="w-full h-16 md:h-24"
            viewBox="0 0 1200 120"
            preserveAspectRatio="none"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M0,60 C300,100 600,20 900,60 C1050,80 1150,40 1200,60 L1200,120 L0,120 Z"
              fill="white"
              opacity="0.3"
            />
            <path
              d="M0,80 C300,120 600,40 900,80 C1050,100 1150,60 1200,80 L1200,120 L0,120 Z"
              fill="white"
              opacity="0.5"
            />
            <path
              d="M0,100 C300,140 600,60 900,100 C1050,120 1150,80 1200,100 L1200,120 L0,120 Z"
              fill="white"
            />
          </svg>
        </div>
        <div className="relative z-10 w-full max-w-6xl mx-auto px-4 md:px-6 lg:px-8 py-12 md:py-16">
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-white/20 mb-4">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-8 w-8 text-white"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                />
              </svg>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Whistleblowing System
            </h1>
            <p className="text-white/80 text-sm md:text-base max-w-xl mx-auto mb-4">
              Saluran pelaporan pelanggaran yang aman dan terpercaya. Identitas
              pelapor dijaga kerahasiaannya.
            </p>
            <div className="flex items-center justify-center gap-2 text-white/90 text-sm md:text-base">
              <Link to="/" className="hover:text-white transition-colors">
                Beranda
              </Link>
              <span className="text-white/60">/</span>
              <span className="text-white">Whistleblowing System</span>
            </div>
          </div>
        </div>
      </div>

      <Content showContentHeader={false}>
        <div className="w-full max-w-3xl mx-auto px-0 md:px-6 lg:px-8 -mt-8 md:-mt-12 relative z-20 pb-12">
          {/* Info Banner */}
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6 flex items-start gap-3">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                clipRule="evenodd"
              />
            </svg>
            <div>
              <p className="text-sm font-semibold text-amber-800 mb-0.5">
                Perlindungan Pelapor
              </p>
              <p className="text-xs text-amber-700 leading-relaxed">
                Sistem ini menjamin kerahasiaan identitas pelapor. Anda dapat
                memilih untuk melaporkan secara anonim. Setiap laporan akan
                ditindaklanjuti secara profesional dan independen.
              </p>
            </div>
          </div>

          {/* Alert Status */}
          {submitStatus === "success" && (
            <div className="bg-green-50 border border-green-200 rounded-xl p-5 mb-6">
              <div className="flex items-start gap-3">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
                <div className="flex-1">
                  <p className="font-semibold text-sm text-green-800">
                    Laporan Berhasil Dikirim!
                  </p>
                  <p className="text-sm mt-0.5 text-green-700">
                    {submitMessage}
                  </p>
                  {reportCode && (
                    <div className="mt-3 bg-white border border-green-200 rounded-lg p-3">
                      <p className="text-xs text-green-700 mb-1">
                        Kode laporan Anda (simpan untuk pengecekan status):
                      </p>
                      <p className="text-lg font-bold text-green-800 tracking-widest">
                        {reportCode}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {submitStatus === "error" && (
            <div className="flex items-start gap-3 bg-red-50 border border-red-200 text-red-800 rounded-xl p-4 mb-6">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5 flex-shrink-0 mt-0.5"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  clipRule="evenodd"
                />
              </svg>
              <div>
                <p className="font-semibold text-sm">Pengiriman Gagal</p>
                <p className="text-sm mt-0.5">{submitMessage}</p>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} noValidate>
            {/* Seksi 1: Identitas Pelapor */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-5">
              <SectionTitle
                title="Identitas Pelapor"
                icon={
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                    />
                  </svg>
                }
              />

              {/* Toggle Anonim */}
              <label className="flex items-center gap-3 mb-5 cursor-pointer select-none">
                <div className="relative">
                  <input
                    type="checkbox"
                    name="is_anonymous"
                    checked={form.is_anonymous}
                    onChange={handleChange}
                    className="sr-only"
                  />
                  <div
                    className={`w-11 h-6 rounded-full transition-colors ${form.is_anonymous ? "bg-[#095393]" : "bg-gray-300"}`}
                  />
                  <div
                    className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${form.is_anonymous ? "translate-x-5" : "translate-x-0"}`}
                  />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-800">
                    Laporkan secara anonim
                  </p>
                  <p className="text-xs text-gray-500">
                    Identitas Anda tidak akan dicantumkan dalam laporan
                  </p>
                </div>
              </label>

              {!form.is_anonymous && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <FormField
                      label="Nama Lengkap Pelapor"
                      required
                      error={errors.nama_pelapor}
                    >
                      <input
                        type="text"
                        name="nama_pelapor"
                        value={form.nama_pelapor}
                        onChange={handleChange}
                        placeholder="Masukkan nama lengkap"
                        className={inputClass(errors.nama_pelapor)}
                      />
                    </FormField>
                  </div>
                  <div className="md:col-span-2">
                    <FormField
                      label="Nomor Handphone"
                      required
                      error={errors.no_hp_pelapor}
                    >
                      <input
                        type="tel"
                        name="no_hp_pelapor"
                        value={form.no_hp_pelapor}
                        onChange={handleChange}
                        placeholder="08xxxxxxxxxx"
                        className={inputClass(errors.no_hp_pelapor)}
                      />
                    </FormField>
                  </div>
                </div>
              )}

              {form.is_anonymous && (
                <div className="flex items-center gap-2 bg-blue-50 border border-blue-100 rounded-lg p-3 text-blue-700 text-xs">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4 flex-shrink-0"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span>
                    Identitas Anda terlindungi. Laporan akan diproses tanpa
                    mencantumkan data pribadi Anda.
                  </span>
                </div>
              )}
            </div>

            {/* Seksi 2: Detail Pelanggaran */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-5">
              <SectionTitle
                title="Detail Pelanggaran"
                icon={
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                    />
                  </svg>
                }
              />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <FormField
                    label="Jenis Pelanggaran"
                    required
                    error={errors.jenis_pelanggaran}
                  >
                    <div className="relative">
                      <select
                        name="jenis_pelanggaran"
                        value={form.jenis_pelanggaran}
                        onChange={handleChange}
                        className={selectClass(errors.jenis_pelanggaran)}
                      >
                        {JENIS_PELANGGARAN.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                      <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-gray-400">
                        <svg
                          className="h-4 w-4"
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path
                            fillRule="evenodd"
                            d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </div>
                    </div>
                  </FormField>
                </div>

                <FormField
                  label="Nama / Jabatan Terlapor"
                  required
                  error={errors.nama_terlapor}
                >
                  <input
                    type="text"
                    name="nama_terlapor"
                    value={form.nama_terlapor}
                    onChange={handleChange}
                    placeholder="Nama atau jabatan pihak yang dilaporkan"
                    className={inputClass(errors.nama_terlapor)}
                  />
                </FormField>

                <FormField
                  label="Jabatan Terlapor (opsional)"
                  error={errors.jabatan_terlapor}
                >
                  <input
                    type="text"
                    name="jabatan_terlapor"
                    value={form.jabatan_terlapor}
                    onChange={handleChange}
                    placeholder="Jabatan pihak yang dilaporkan"
                    className={inputClass(errors.jabatan_terlapor)}
                  />
                </FormField>

                <FormField
                  label="Lokasi Kejadian (opsional)"
                  error={errors.lokasi_kejadian}
                >
                  <input
                    type="text"
                    name="lokasi_kejadian"
                    value={form.lokasi_kejadian}
                    onChange={handleChange}
                    placeholder="Tempat terjadinya pelanggaran"
                    className={inputClass(errors.lokasi_kejadian)}
                  />
                </FormField>

                <FormField
                  label="Tanggal Kejadian (opsional)"
                  error={errors.tanggal_kejadian}
                >
                  <input
                    type="date"
                    name="tanggal_kejadian"
                    value={form.tanggal_kejadian}
                    onChange={handleChange}
                    max={new Date().toISOString().split("T")[0]}
                    className={inputClass(errors.tanggal_kejadian)}
                  />
                </FormField>

                <div className="md:col-span-2">
                  <FormField
                    label="Uraian Kejadian"
                    required
                    error={errors.uraian_kejadian}
                  >
                    <textarea
                      name="uraian_kejadian"
                      value={form.uraian_kejadian}
                      onChange={handleChange}
                      placeholder="Jelaskan kronologi dan detail pelanggaran yang Anda ketahui secara lengkap..."
                      rows={6}
                      className={`${inputClass(errors.uraian_kejadian)} resize-none`}
                    />
                    <p className="text-xs text-gray-400 mt-1">
                      Semakin detail uraian, semakin mudah laporan
                      ditindaklanjuti
                    </p>
                  </FormField>
                </div>
              </div>
            </div>

            {/* Submit */}
            <div className="flex flex-col sm:flex-row gap-3">
              <Link
                to="/"
                className="flex-1 text-center py-3 px-6 border border-gray-300 rounded-lg text-sm font-semibold text-gray-700 hover:bg-gray-50 transition-colors no-underline"
              >
                Batal
              </Link>
              <button
                type="submit"
                disabled={isLoading}
                className="flex-1 flex items-center justify-center gap-2 py-3 px-6 bg-[#095393] hover:bg-[#074a7a] text-white rounded-lg text-sm font-semibold transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {isLoading ? (
                  <>
                    <svg
                      className="animate-spin h-4 w-4"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                      />
                    </svg>
                    Mengirim...
                  </>
                ) : (
                  <>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-4 w-4"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                      />
                    </svg>
                    Kirim Laporan
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </Content>
    </>
  );
}

export default WhistleblowerSystem;
